import { useState } from 'react'
import { daysBetween } from '@app/shared'

import {
  useClock,
  useLearningItems,
  useLearningResources,
  useLearningStreak,
  useLearningTrackSummaries,
} from '../lib/data'
import { DataModeBadge } from '../components/HeaderBand'
import { ActivityHeatmap, ActivityHeatmapSkeleton } from '../components/learning/ActivityHeatmap'
import { LearningBand, Unavailable } from '../components/learning/Chrome'
import {
  CopyForAIButton,
  CopyForAIFallback,
  useCopyForAI,
} from '../components/learning/CopyForAI'
import { ResourceLibrary, ResourceLibrarySkeleton } from '../components/learning/ResourceLibrary'
import { SessionStrip, SessionStripSkeleton } from '../components/learning/SessionStrip'
import { TodayBoard, TodayBoardSkeleton } from '../components/learning/TodayBoard'
import { TrackCard, TrackCardSkeleton } from '../components/learning/TrackCard'
import { Flame, Shelf } from '../components/learning/icons'
import { planWeeks } from '../components/learning/derive'

/**
 * LEARNING — rebuilt 2026-08-28 around one sentence from the owner: "I just go to the learning
 * page and start learning as quickly as possible. The page keeps track of my learning but is also
 * my direct link to my learning resources."
 *
 * THE THREE ZONES, in the order he reads them:
 *
 *   1. TODAY      — the checklist. Every item the plans want today, each with a checkbox, the
 *                   plan's minutes estimate, and a button that opens the exact thing to open.
 *                   Expanding a row is the workspace: the instructions, the link again in full,
 *                   and logging the session (minutes + note). This is the top of the page because
 *                   it is the only part of it that is doing rather than measuring.
 *   2. THE TRACKS — the cards that already worked: ring, pace phrase, week strip, next item.
 *                   Reflowed under Today rather than deleted; they are how the plan is measured,
 *                   and the measurement is not the point of the visit.
 *   3. THE RUN    — the combined activity heatmap and the seven-day minutes strip.
 *
 * AND A SECOND SECTION HE CLICKS INTO: Resources, the library page — every link his plans point
 * at, per track. A tab rather than a route, because `/learning/:trackId` already owns the segment
 * a `/learning/resources` path would need and a track slugged "resources" would then open the
 * wrong screen.
 *
 * THE PAGE SCROLLS NOW, and the previous version's comment saying it does not is gone with it.
 * Today plus two cards plus two strips does not fit 900px, and a fixed-height layout that clipped
 * the heatmap would be hiding data to keep a promise about geometry.
 *
 * EVERY NUMBER HERE IS STILL DERIVED IN THE CONTRACT, not in this file — the percentage, the pace
 * phrase, the current unit, the week counts, the library's per-link counts. A component that
 * computed its own would be a second answer to a question the Worker already answers.
 */
export function Learning() {
  const [tab, setTab] = useState<'today' | 'resources'>('today')
  // The whole domain as markdown, for a chat outside the app. It costs nothing until it is
  // pressed — see components/learning/CopyForAI.tsx.
  const copy = useCopyForAI()

  const { data: clock } = useClock()
  const today = clock?.date

  const { data: summaries, isPending, error } = useLearningTrackSummaries()
  const { data: items, isPending: itemsPending, error: itemsError } = useLearningItems(today)
  const { data: streak, isPending: streakPending, error: streakError } = useLearningStreak()
  const {
    data: libraries,
    isPending: librariesPending,
    error: librariesError,
  } = useLearningResources()

  // The arc is the LONGEST plan's length and the week is counted from the earliest plan start.
  // Two tracks of different lengths cannot share one arc, so the band names the longer one rather
  // than averaging two numbers into a third that describes neither.
  const arc = Math.max(0, ...(summaries ?? []).map((s) => planWeeks(s.track.planStartDate, s.track.planEndDate)))
  const earliest = (summaries ?? []).map((s) => s.track.planStartDate).sort()[0]
  const week = today && earliest ? Math.floor(daysBetween(earliest, today) / 7) + 1 : 0

  return (
    <>
      <LearningBand>
        <div className="flex min-w-0 flex-none items-baseline gap-[9px] whitespace-nowrap">
          <span className="text-screen font-semibold">Learning</span>
          {summaries && (
            <span className="text-[11.5px] font-normal text-muted">
              {summaries.length} {summaries.length === 1 ? 'track' : 'tracks'} · {arc}-week arc ·
              week {week}
            </span>
          )}
        </div>

        {/* The streak chip is the combined one, and it is ABSENT rather than zero when nothing is
            running — a "0-day streak" is a scold, and the design has no scolds in it. */}
        {streak && streak.current > 0 && (
          <span className="flex flex-none items-center gap-[7px] rounded-pill border border-line bg-surface px-3 py-[6px]">
            <Flame size={13} strokeWidth={1.8} className="text-orange-2" />
            <span className="text-label font-semibold text-ink-2">{streak.current}-day streak</span>
          </span>
        )}

        <div className="ml-auto flex flex-none items-center gap-[7px]">
          {/* Today only: the export is the checklist's data, and the Resources tab is a page of
              links that says nothing about pace, sessions or scores. */}
          {tab === 'today' && <CopyForAIButton copy={copy} />}
          <div className="flex flex-none items-center gap-[3px] rounded-pill border border-line bg-surface p-[3px]">
            <Tab active={tab === 'today'} onClick={() => setTab('today')}>
              Today
            </Tab>
            <Tab active={tab === 'resources'} onClick={() => setTab('resources')}>
              <Shelf size={12} className={tab === 'resources' ? 'text-surface' : 'text-muted'} />
              Resources
            </Tab>
          </div>
          <DataModeBadge />
        </div>
      </LearningBand>

      <div className="flex min-h-0 flex-1 flex-col gap-[14px] overflow-y-auto px-[30px] pb-[26px]">
        {tab === 'today' ? (
          <>
            {/* The copy that did not happen, said out loud with the text offered by hand. It
                renders nothing unless the clipboard actually refused. */}
            <CopyForAIFallback copy={copy} />

            {/* ZONE 1 — the checklist. First, always, and it waits on both reads rather than
                drawing half a board: a row that appeared a beat later would move the one under
                the cursor. */}
            {(itemsPending || isPending) && <TodayBoardSkeleton />}
            <Unavailable error={itemsError} />
            {items && summaries && today && (
              <TodayBoard items={items} summaries={summaries} today={today} />
            )}

            {/* ZONE 2 — the tracks. */}
            {isPending && (
              <div className="grid flex-none grid-cols-2 gap-[14px]">
                <TrackCardSkeleton />
                <TrackCardSkeleton />
              </div>
            )}
            <Unavailable error={error} />

            {summaries && summaries.length > 0 && today && (
              <div className="grid flex-none grid-cols-2 gap-[14px]">
                {summaries.map((summary, i) => (
                  <TrackCard key={summary.track.id} summary={summary} index={i} today={today} />
                ))}
              </div>
            )}

            {/* Zero tracks is a real answer and a different one from "the read failed". It gets the
                sentence that names what is missing and the one thing that would fix it. */}
            {summaries && summaries.length === 0 && (
              <div className="rounded-context border border-line bg-surface px-5 py-[18px] shadow-card">
                <p className="max-w-[620px] text-body text-ink-3 text-pretty">
                  No plan has been imported yet, so there is nothing to pace against. Plans are
                  imported rather than written here — bring one in and this screen fills itself.
                </p>
              </div>
            )}

            {/* ZONE 3 — the run. */}
            {streakPending && <ActivityHeatmapSkeleton />}
            {streak && today && <ActivityHeatmap streak={streak} today={today} />}
            {/* The heatmap and the strip read the same endpoint, so one sentence covers both rather
                than the same failure being printed twice down the page. */}
            <Unavailable error={streakError} />

            {streakPending && <SessionStripSkeleton />}
            {streak && today && <SessionStrip streak={streak} today={today} />}
          </>
        ) : (
          <>
            {librariesPending && <ResourceLibrarySkeleton />}
            <Unavailable error={librariesError} />
            {libraries && <ResourceLibrary tracks={libraries} />}
          </>
        )}
      </div>
    </>
  )
}

/** The two-way switch in the band. `aria-pressed`, not a link: nothing about it is addressable. */
function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'mo-press flex flex-none items-center gap-[5px] whitespace-nowrap rounded-pill px-[11px] py-[5px] text-micro leading-none',
        active ? 'bg-ink font-semibold text-surface' : 'font-medium text-ink-3 hover:text-ink',
      ].join(' ')}
    >
      {children}
    </button>
  )
}
